import React, { useState, useEffect } from 'react';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Plus, Upload } from 'lucide-react';
import { Label } from '../../../../../components/ui/label';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import customNotification from '../../../../../components/ui/custom-notification';
import { useTeamMembersReduxHook } from '../../../hooks/useTeamMembersRedux';
import { AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { getCertificatePresignedUrl } from '../../../../../redux/TeamMembers/saga';

interface TeamMemberCertificateUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (files: FileList | null) => void;
    title?: string;
    acceptedTypes?: string;
    multiple?: boolean;
    maxFiles?: number;
    buttonText?: string;
    label?: string;
    description?: string;
    required?: boolean;
    teamMember?: any;
    projectId?: number;
}

export const TeamMemberCertificateUploadModal: React.FC<TeamMemberCertificateUploadModalProps> = ({
    isOpen,
    onClose,
    onUpload,
    title = 'Upload Team Member Certificates',
    acceptedTypes = '.pdf,.doc,.docx,.xlsx,.xls',
    multiple = false,
    maxFiles = 1,
    buttonText = 'Select Certificate',
    label = 'Certificate',
    description = 'Upload team member certificates (PDF, Excel, DOC, etc...)',
    required = true,
    teamMember,
    projectId
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedTags, setSelectedTags] = useState<{ [fileIndex: number]: string[] }>({});
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [isProcessingDocument, setIsProcessingDocument] = useState(false);
    
    // Use the same Redux hook approach as AddTeamMemberModal
    const { 
        createUpdateTeamMember, 
        certifications: reduxCertifications,
        fetchCertifications 
    } = useTeamMembersReduxHook(undefined, projectId);
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Use the same safeCertifications approach as AddTeamMemberModal
    const safeCertifications = reduxCertifications && reduxCertifications.length > 0 ? reduxCertifications : [];

    // Convert certifications to tag options format for FileUpload component
    const tagOptions = safeCertifications
        ?.filter((cert: any) => cert && cert.value && cert.label)
        .map((cert: any) => ({
            value: cert.value,
            label: cert.label
        })) || [];

    // Load certifications when component mounts
    useEffect(() => {
        if (projectId) {
            fetchCertifications({ projectId });
        }
    }, [projectId, fetchCertifications]);

    // Handle file select for FileUpload component
    const handleFileSelect = (selectedFiles: FileList | null) => {
        if (selectedFiles && selectedFiles.length > 0) {
            const fileArray = Array.from(selectedFiles);
            const updatedFiles = [...files, ...fileArray];
            setFiles(updatedFiles);
        }
    };

    // Handle remove file
    const handleRemoveFile = (index: number) => {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        
        // Update selectedTags to remove tags for removed file
        const updatedTags: { [fileIndex: number]: string[] } = {};
        updatedFiles.forEach((_, i) => {
            if (i < index) {
                updatedTags[i] = selectedTags[i] || [];
            } else if (i > index) {
                updatedTags[i - 1] = selectedTags[i] || [];
            }
        });
        setSelectedTags(updatedTags);
    };

    // Handle tags change for FileUpload component
    const handleTagsChange = (fileIndex: number, tags: string[]) => {
        setSelectedTags(prev => ({
            ...prev,
            [fileIndex]: tags
        }));
    };

    // Direct upload function using team member saga function
    const uploadDocumentDirectly = async (file: File): Promise<{ s3Key: string; fileName: string } | null> => {
        try {
            // Get presigned URL using team member saga function
            const presignedData = await getCertificatePresignedUrl({
                fileName: file.name,
                contentType: file.type,
                fileSize: file.size,
                projectId: projectId!,
                userId: teamMember?.user_id || teamMember?.userId,
            });

            if (!presignedData?.presignedUrl || !presignedData?.s3Key) {
                throw new Error('Invalid presigned URL response');
            }

            const { presignedUrl: uploadUrl, s3Key } = presignedData;

            // Upload to S3 using presigned URL
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!uploadResponse.ok) {
                throw new Error(`S3 upload failed: ${uploadResponse.status}`);
            }

            return { s3Key, fileName: file.name };
        } catch (error) {
            throw error;
        }
    };

    const handleUpload = async () => {
        if (files && files.length > 0 && teamMember && projectId) {
            if (isProcessingDocument) {
                customNotification.warning('Upload in Progress', 'Please wait for the current upload to complete.');
                return;
            }

            setIsUploading(true);
            setIsProcessingDocument(true);
            
            try {
                const newCertificates: any[] = [];
                
                // Upload each file and collect certificates
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const fileTags = selectedTags[i] || [];
                    
                    try {
                        const uploadResult = await uploadDocumentDirectly(file);
                        
                        if (uploadResult) {
                            const certificate = {
                                file_name: uploadResult.fileName,
                                document_tags: fileTags,
                                s3Key: uploadResult.s3Key,
                                user_id: teamMember.user_id || teamMember.userId,
                                team_member_id: teamMember.team_member_id || teamMember.id
                            };
                            
                            newCertificates.push(certificate);
                        }
                    } catch (uploadError) {
                        customNotification.error('Upload Failed', `Failed to upload ${file.name}. Please try again.`);
                    }
                }
                
                // Create team member update payload if we have certificates
                if (newCertificates.length > 0) {
                    const updatePayload = {
                        team_member_id: teamMember.team_member_id || teamMember.id,
                        user_id: teamMember.user_id || teamMember.userId,
                        project_id: projectId,
                        team_member_role: teamMember.role,
                        project_level_role_id: teamMember.apiData?.project_level_role_id,
                        status: teamMember.status || 'Active',
                        certificates: newCertificates
                    };

                    await createUpdateTeamMember(updatePayload);
                    
                    // Success notification is already shown by the createUpdateTeamMember saga
                    
                    onUpload(files as unknown as FileList);
                    setFiles([]);
                    setSelectedTags({});
                    setIsUploading(false);
                    setIsProcessingDocument(false);
                    onClose();
                } else {
                    setIsUploading(false);
                    setIsProcessingDocument(false);
                }
            } catch (error) {
                customNotification.error(
                    'Upload failed', 
                    'There was an error uploading the certificates. Please try again.'
                );
                setIsUploading(false);
                setIsProcessingDocument(false);
            }
        }
    };

    const handleClose = () => {
        setFiles([]);
        setIsUploading(false);
        setSelectedTags({});
        setIsProcessingDocument(false);
        onClose();
    };

    // Reset form inputs when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFiles([]);
            setIsUploading(false);
            setSelectedTags({});
            setIsAddingTag(false);
            setIsProcessingDocument(false);
        }
    }, [isOpen]);

    const handleAddTag = async (tagName: string) => {
        if (tagName.trim()) {
            try {
                if (safeCertifications.find((tag: any) => tag.label === tagName.trim())) {
                    customNotification.warning(`${tagName.trim()} certificate already exists`);
                    return;
                }
                // Use the same API as AddTeamMemberModal for adding new tags
                const apiPayload = {
                    tags_name: tagName.trim(),
                    project_id: projectId,
                    tags_type: 'Certificate'
                };
                
                const res = await AddMaterialCategory(apiPayload, 'certificate tag');
                if (res) {
                    // Refresh certifications to get the new tag
                    await fetchCertifications({ projectId });
                    // Success message is already shown by AddMaterialCategory
                    
                    // Auto-select the newly created tag for the first file if it exists
                    if (files.length > 0) {
                        const newTagValue = tagName.trim();
                        setSelectedTags(prev => ({
                            ...prev,
                            [0]: [...(prev[0] || []), newTagValue]
                        }));
                    }
                    
                    // Close the tag dialog
                    setIsAddingTag(false);
                }
            } catch (error) {
                customNotification.error('Failed to Add Certificate Tag', 'There was an error adding the new certificate tag. Please try again.');
            }
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] w-[90vw]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Upload className="h-5 w-5" />
                        {title}
                    </DialogTitle>
                </DialogHeader>

                <div className="py-6 space-y-6">
                    <div className="space-y-2">
                        <Label>
                            {label || 'Upload Certificate'}
                            {required && <span className="text-red-500">*</span>}
                        </Label>
                        {description && (
                            <p className="text-sm text-gray-600">
                                {description}
                            </p>
                        )}

                        {!projectId ? (
                            <div className="p-3 text-center text-gray-500 border rounded-md">
                                <span>Project ID is required to upload certificates</span>
                            </div>
                        ) : (
                            <FileUpload
                                onFileSelect={handleFileSelect}
                                accept={acceptedTypes}
                                multiple={multiple}
                                maxFiles={maxFiles}
                                buttonText={buttonText}
                                variant="outline"
                                size="lg"
                                onRemoveFile={handleRemoveFile}
                                selectedFiles={files}
                                showFileList={files.length > 0}
                                className="w-full"
                                maxHeight=""
                                showTags={true}
                                optionsTags={tagOptions}
                                selectedTags={selectedTags}
                                showAddButton={true}
                                addButtonText="Add New Tag"
                                onAddButtonClick={() => setIsAddingTag(true)}
                                onTagsChange={handleTagsChange}
                            />
                        )}

                        {/* Debug: Show current files count */}
                        <div className="text-xs text-gray-500">
                            Current files: {files.length} file(s)
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex gap-2 pt-4">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        className="flex items-center gap-2"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={!files || files.length === 0 || isUploading}
                        className="flex items-center gap-2"
                    >
                        {isUploading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="h-4 w-4" />
                                Upload
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
            
            {/* Add New Tag Dialog */}
            <AddNewItemDialog
                open={isAddingTag}
                onOpenChange={setIsAddingTag}
                title="Add New Certification"
                label="Certification Name"
                placeholder="Enter certification name"
                onAdd={handleAddTag}
                buttonText="Add Certification"
                cancelText="Cancel"
            />
        </Dialog>
    );
}; 